import { AxiosResponse } from "axios";
import FileSaver from "file-saver";
import { Guid } from "guid-typescript";
import isRelativeUrl from "is-relative-url";
import { store } from "react-notifications-component";
import mimeTypeMappings from "../json/mime-types-mappings.json";
import { BASE_64_REGEX } from "./constants";
import { KuikaAppManager } from "./kuika-app-manager";

export class KMainFunctions {
  static virtualDirectoryURL: string = "auto"; // Use by code generation

  // NOTIFY ACTION DEFAULT DURATIONS
  static NOTIFY_ACTION_DEFAULT_DEFAULT_DURATION = 5;

  static NOTIFY_ACTION_DEFAULT_SUCCESS_DURATION = 5;

  static NOTIFY_ACTION_DEFAULT_INFO_DURATION = 7;

  static NOTIFY_ACTION_DEFAULT_WARNING_DURATION = 15;

  static NOTIFY_ACTION_DEFAULT_ERROR_DURATION = 0;

  static get windowLocationOrigin() {
    let origin = window?.location?.origin;
    if (window?.location?.protocol === "file:") {
      origin = `file://${window.location.pathname}`;
      return origin;
    }
    return KMainFunctions.virtualDirectoryURL === "auto" ? origin : `${origin}${KMainFunctions.virtualDirectoryURL}`;
  }

  static exceptionHandler(e) {
    const msg = e?.response?.data?.message;
    if (msg) {
      this.displayErrorNotification(msg);
    } else if (e?.message) {
      console.error(e.message);
      this.displayErrorNotification(e.message);
    }
    if (e?.stack) {
      console.error(e.stack);
    }
  }

  static displaySuccessNotification(message, params?) {
    store.addNotification({
      title: params?.title ? params.title : "Success!",
      message,
      type: "success",
      container: params?.container ? params.container : "bottom-right",
      animationIn: params?.animationIn ? params.animationIn : ["animated", "flipInX"],
      animationOut: params?.animationOut ? params.animationOut : ["animated", "fadeOut"],
      dismiss: {
        duration: params?.duration ? params.duration : this.NOTIFY_ACTION_DEFAULT_SUCCESS_DURATION * 1000,
        pauseOnHover: params?.pauseOnHover ? params.pauseOnHover : true,
        showIcon: params?.showIcon ? params.showIcon : true
      }
    });
  }

  static ignoreKnownConsoleWarns(): void {
    if (!window.location.href.includes("localhost")) {
      const ignoredWarnings: string[] = [
        "[webpack-dev-server",
        "Google Maps JavaScript",
        "allow-scripts and allow-same-origin",
        "It looks like there are several",
        "styled-components",
        "link preload but not used within a few second",
        "react-fast-compare cannot handle circular refs",
        "componentWillReceiveProps has been renamed"
      ];
      const originalConsoleWarns = console.warn;
      console.warn = function (...args) {
        if (args[0] && ignoredWarnings.some((element) => args[0].toString().includes(element))) {
          return;
        }
        originalConsoleWarns.apply(console, args);
      };
    }
  }

  static ignoreKnownConsoleErrors(): void {
    if (!window.location.href.includes("localhost")) {
      const ignoredErrors: string[] = [
        "is deprecated in StrictMode",
        "Invalid DOM property",
        "X-Frame-Options may only be",
        "spell it as lowercase",
        "A valid state object (or null) must be returned",
        "Each child in a list should have a unique",
        "or a non-boolean attribute",
        "Invalid value for prop",
        "is not connected to any Form element.",
        "onResize is not a function",
        "listener to be a function",
        "Unknown event handler property",
        "validateDOMNesting(...)",
        "Failed prop type: Invalid prop"
      ];
      const originalConsoleErrors = console.error;
      console.error = function (...args) {
        if (args[0] && ignoredErrors.some((element) => args[0].toString().includes(element))) {
          return;
        }
        originalConsoleErrors.apply(console, args);
      };
    }
  }

  static displayWarningNotification(message, params?) {
    store.addNotification({
      title: params?.title ? params.title : "Warning!",
      message,
      type: "warning",
      container: params?.container ? params.container : "bottom-right",
      animationIn: params?.animationIn ? params.animationIn : ["animated", "flipInX"],
      animationOut: params?.animationOut ? params.animationOut : ["animated", "fadeOut"],
      dismiss: {
        duration: params?.duration ? params.duration : this.NOTIFY_ACTION_DEFAULT_WARNING_DURATION * 1000,
        pauseOnHover: params?.pauseOnHover ? params.pauseOnHover : true,
        showIcon: params?.showIcon ? params.showIcon : true
      }
    });
  }

  static displayErrorNotification(message, params?) {
    if (message) {
      store.addNotification({
        title: params?.title ? params.title : "Error!",
        message,
        type: "danger",
        container: params?.container ? params.container : "bottom-right",
        animationIn: params?.animationIn ? params.animationIn : ["animated", "flipInX"],
        animationOut: params?.animationOut ? params.animationOut : ["animated", "fadeOut"],
        dismiss: {
          duration: params?.duration ? params.duration : this.NOTIFY_ACTION_DEFAULT_ERROR_DURATION * 1000,
          pauseOnHover: params?.pauseOnHover ? params.pauseOnHover : true,
          showIcon: params?.showIcon ? params.showIcon : true
        }
      });
    }
  }

  static toCamelCase(value: string): string {
    if (!value || (this.isLetter(value[0]) && !this.isUpperCase(value[0]))) {
      return value;
    }

    let prefix = "";
    let chars = value.split("");

    if (!this.isLetter(value[0])) {
      prefix = value[0];
      chars = value.slice(1).split("");
    }

    for (let i = 0; i < chars.length; i++) {
      if (i === 1 && !this.isUpperCase(chars[i])) {
        break;
      }
      const hasNext = i + 1 < chars.length;

      // Stop when next char is already lowercase.
      if (i > 0 && hasNext && !this.isUpperCase(chars[i + 1])) {
        // If the next char is a space, lowercase current char before exiting.
        if (chars[i + 1] === " ") {
          chars[i] = chars[i].toLowerCase();
        }
        break;
      }

      chars[i] = chars[i].toLowerCase();
    }

    return prefix + chars.join("");
  }

  // Static helper method to check if a character is a letter
  private static isLetter(char: string): boolean {
    return /^[a-zA-Z]$/.test(char);
  }

  // Static helper method to check if a character is uppercase
  private static isUpperCase(char: string): boolean {
    if (!this.isLetter(char)) {
      return false;
    }

    return char === char.toUpperCase();
  }

  static IsResStatus2XX(res: AxiosResponse): boolean {
    return !!(res?.status && res.status >= 200 && res.status < 300);
  }

  static getTextAlignAsJustifyContent(value: string) {
    if (value) {
      if (value.includes("center")) return "center";
      if (value.includes("right")) return "flex-end";
      if (value.includes("left")) return "flex-start";
    }
  }

  static setChildWidthAndAlignment(child) {
    const kuikablockstyle = child.getAttribute("kuikablockstyle");
    const componentname = child.getAttribute("kuikacomponentname");
    const childClassName = child.className;
    if (child.parentElement.className?.includes("ant-table-cell") && child.className?.includes("ant-space")) {
      child.style.height = "auto";
    }
    if (
      kuikablockstyle === "2" ||
      componentname === "Date" ||
      childClassName?.includes("kuika_table__wrapper") ||
      childClassName?.includes("ant-form-item-row") ||
      childClassName?.includes("kFormItem") ||
      childClassName?.includes("ant-row") ||
      childClassName?.startsWith("kuikaStepperComponent") ||
      child?.style?.display === "block"
    ) {
      child.style.width = "100%";
    } else {
      child.style.alignSelf = "center";
    }
  }

  static setKFormItemStyles(child) {
    const imageInputs = child.querySelectorAll(".kuika-image-input");
    for (let i = 0; i < imageInputs.length; i++) {
      const imageInput = imageInputs[i];
      const kFormItemParent = imageInput.closest(".kFormItem");
      if (kFormItemParent) {
        kFormItemParent.style.width = "auto";
        kFormItemParent.style.alignSelf = "center";
      }
    }
  }

  static setDisplayBlockForImages(node) {
    const images = node.querySelectorAll("[kuikacomponentname='Image']");
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      if (image && image.children && image.children[0]) {
        image.children[0].style.display = "block";
      }
    }
  }

  static handleAlignments(node: any, styles: any, isInline?: boolean) {
    if (styles && styles.alignItems && node && node.style) {
      node.style.display = isInline ? "inline-flex" : "flex";
      node.style.flexWrap = "wrap";
      node.style.placeContent = styles.alignItems;

      if (node.children) {
        for (let i = 0; i < node.children.length; i++) {
          const child = node.children[i];
          if (child) {
            this.setChildWidthAndAlignment(child);
            this.setKFormItemStyles(child);
          }
        }
        this.setDisplayBlockForImages(node);
      }
    }

    if (styles && styles.alignItems && styles.textAlign && node && node.style) {
      const justify = KMainFunctions.getTextAlignAsJustifyContent(styles.textAlign);
      node.style.justifyContent = justify;
    } else if (styles && styles.alignItems && !styles.textAlign && node && node.style) {
      node.style.justifyContent = "flex-start";
    }

    return node;
  }

  static isValidUrl(url: string) {
    if (!url) return false;

    try {
      // eslint-disable-next-line no-new
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  static isRelativeUrl(url: string): boolean {
    return isRelativeUrl(url);
  }

  static isBase64(str: string) {
    if (!str) return false;
    return BASE_64_REGEX.test(str);
  }

  public static openBase64Pdf(data: string, warnLandscape: boolean, openAs: "newTab" | "currentPage"): void {
    const base64WithoutPrefix = data.substring("data:application/pdf;base64,".length);
    const bytes = atob(base64WithoutPrefix);
    let { length } = bytes;
    const out = new Uint8Array(length);

    while (length--) {
      out[length] = bytes.charCodeAt(length);
    }

    const url = URL.createObjectURL(new Blob([out], { type: "application/pdf" }));
    KMainFunctions.openUrlPdf(url, openAs);
  }

  public static openUrlPdf(url: string, openAs: "newTab" | "currentPage"): void {
    if (openAs === "currentPage") {
      window.open(url, "_self")?.focus();
      return;
    }

    // openAs === "newTab"
    window.open(url, "_blank")?.focus();
  }

  public static downloadFile(data: string | Blob, fileName: string): void {
    FileSaver.saveAs(data, fileName);
  }

  public static async toBlob(dataUrl: string): Promise<Blob> {
    const response = await fetch(dataUrl);
    return response.blob();
  }

  public static getBase64UrlPrefix(mimeType: string): string {
    return `data:${mimeType};base64,`;
  }

  public static getUrlByWithResourceId = (
    guid: string,
    width?: string | number,
    height?: string | number,
    imageFit?: 0 | 1 | 2
  ) => {
    const url: URL = new URL(`${KuikaAppManager.GetBackendUrl()}/resource/runtime/byid`);

    url.searchParams.append("resourceId", guid);

    if (width && typeof width === "string" && !width?.includes("%") && !width?.includes("vw")) {
      url.searchParams.append("width", width.toString());
    }

    if (height && typeof height === "string" && !height?.includes("%") && !height?.includes("vh")) {
      url.searchParams.append("height", height.toString());
    }

    if (imageFit) {
      url.searchParams.append("imageFit", imageFit.toString());
    }

    return url.toString();
  };

  public static getExtensionByMimeType(mimeType: string): string {
    const extension = mimeTypeMappings[mimeType];

    if (!extension) {
      return "";
    }

    return extension;
  }

  public static generateFilenameForDownloadFile(filename: string, extension: string): string {
    const isExtensionValid: boolean = extension && extension !== "" && extension !== "undefined";
    const isFilenameValid: boolean = filename && filename !== "" && filename !== "undefined";

    if (isFilenameValid && filename.endsWith(`.${extension}`)) {
      return filename;
    }

    if (!isFilenameValid && isExtensionValid) {
      return `${Guid.create().toString()}.${extension}`;
    }

    if (isFilenameValid && !isExtensionValid) {
      return filename;
    }

    if (isFilenameValid && isExtensionValid) {
      return `${filename}.${extension}`;
    }

    return Guid.create().toString();
  }

  public static getIsValidHttpUrl(value: unknown): boolean {
    if (typeof value !== "string") {
      return false;
    }

    let url: URL;

    try {
      url = new URL(value);
    } catch (_) {
      return false;
    }

    return url.protocol === "http:" || url.protocol === "https:";
  }

  public static convertBlobToBase64(blob: Blob): Promise<string | ArrayBuffer> {
    const reader = new FileReader();
    reader.readAsDataURL(blob);

    return new Promise((resolve) => {
      reader.onloadend = () => {
        resolve(reader.result);
      };
    });
  }
}
